import {
  BridgeMessageType,
  composeBlocksForPageView,
  hydrateLayoutBlocksWithPersona,
  isShellToSandboxMessage,
  type LayoutBlock,
  type ShellToSandboxMessage,
} from '@web-studio/shared-schemas';
import { useSandboxPreviewStore } from '@/previewStore';
import {
  handleContentPatchMessage,
  handleDesignTokensMessage,
  handleMediaPatchMessage,
} from '@/bridge/handlers';
import { isAllowedShellOrigin } from '@/bridge/shellOrigin';
import { postSandboxSyncedToShell } from '@/utils/postSandboxSynced';

const pendingShellMessages: ShellToSandboxMessage[] = [];
let bridgeMounted = false;

declare global {
  interface Window {
    __wsPendingShellMessages?: ShellToSandboxMessage[];
  }
}

function captureEarlyShellMessage(event: MessageEvent): void {
  if (bridgeMounted || !isAllowedShellOrigin(event.origin)) {
    return;
  }
  if (!isShellToSandboxMessage(event.data)) {
    return;
  }
  pendingShellMessages.push(event.data);
}

if (typeof window !== 'undefined') {
  window.addEventListener('message', captureEarlyShellMessage);
}

type BlocksSyncMessage = Extract<
  ShellToSandboxMessage,
  { type: typeof BridgeMessageType.BLOCKS_SYNC }
>;

function hydrateBlocksSyncLists(message: BlocksSyncMessage): {
  blocks: LayoutBlock[];
  globalBlocks?: LayoutBlock[];
  pageBlocks?: LayoutBlock[];
} {
  const locales = [
    ...new Set(
      [message.locale, message.defaultLocale, ...(message.supportedLocales ?? []), 'de'].filter(
        Boolean,
      ) as string[],
    ),
  ];
  const opts = { businessName: message.businessName ?? '', locales };
  const persona = message.personaBranding;

  const globalBlocks = message.globalBlocks
    ? hydrateLayoutBlocksWithPersona(message.globalBlocks, persona, opts)
    : undefined;
  const pageBlocks = message.pageBlocks
    ? hydrateLayoutBlocksWithPersona(message.pageBlocks, persona, opts)
    : undefined;
  const blocks = hydrateLayoutBlocksWithPersona(message.blocks, persona, opts);

  return { blocks, globalBlocks, pageBlocks };
}

function resolveBlocksSync(
  message: BlocksSyncMessage,
  hydrated: { blocks: LayoutBlock[]; globalBlocks?: LayoutBlock[]; pageBlocks?: LayoutBlock[] },
) {
  if (
    message.activePageId &&
    hydrated.globalBlocks &&
    hydrated.pageBlocks &&
    hydrated.blocks.length === 0
  ) {
    // Studio Zone 3 keeps draft-hidden blocks (muted in SitePage); live bake omits them.
    return composeBlocksForPageView(
      {
        globalBlocks: hydrated.globalBlocks,
        pageBlocks: { [message.activePageId]: hydrated.pageBlocks },
      },
      message.activePageId,
      { includeHidden: true },
    );
  }
  return hydrated.blocks;
}

function handleShellMessage(message: ShellToSandboxMessage): void {
  const store = useSandboxPreviewStore.getState();

  switch (message.type) {
    case BridgeMessageType.ACTIVE_BLOCK: {
      const incomingTarget = message.editTarget ?? null;
      const { activeBlockId, activeEditTarget } = store;
      const hasChildTarget =
        activeEditTarget?.kind === 'media-field' ||
        activeEditTarget?.kind === 'content-field' ||
        activeEditTarget?.kind === 'menu-item' ||
        activeEditTarget?.kind === 'menu-column';

      // Keep optimistic child selection (e.g. media click) when shell echoes a
      // block-only ACTIVE_BLOCK before BLOCK_SELECT is applied.
      if (message.blockId === activeBlockId && incomingTarget === null && hasChildTarget) {
        break;
      }
      store.setActiveBlockId(message.blockId, incomingTarget);
      break;
    }
    case BridgeMessageType.LOCALE:
      store.setLocale(message.locale);
      break;
    case BridgeMessageType.WORKSPACE_MODE:
      store.setInteractionMode(message.mode);
      break;
    case BridgeMessageType.BLOCKS_SYNC: {
      store.setLocale(message.locale);
      store.setShowInvisibleBlocks(message.showInvisibleBlocks ?? true);
      const hydrated = hydrateBlocksSyncLists(message);
      const blocks = resolveBlocksSync(message, hydrated);
      if (
        message.activePageId &&
        hydrated.globalBlocks &&
        hydrated.pageBlocks
      ) {
        store.setScopedBlocks({
          blocks,
          globalBlocks: hydrated.globalBlocks,
          canvasBlocks: hydrated.pageBlocks,
          activePageId: message.activePageId,
          pages: message.pages,
          siteMode: message.siteMode,
          vertical: message.vertical,
          defaultLocale: message.defaultLocale,
          supportedLocales: message.supportedLocales,
          themeId: message.themeId,
          colorsetId: message.colorsetId,
          customColorSets: message.customColorSets,
          spacingEnum: message.spacingEnum,
          cornerEnum: message.cornerEnum,
          typographyEnum: message.typographyEnum,
          ...('hasTastingTimeline' in message
            ? { hasTastingTimeline: Boolean(message.hasTastingTimeline) }
            : {}),
          ...('personaBranding' in message
            ? { personaBranding: message.personaBranding ?? null }
            : {}),
          ...('businessName' in message ? { businessName: message.businessName ?? '' } : {}),
        });
      } else {
        store.setBlocks(
          blocks,
          message.themeId,
          message.colorsetId,
          message.spacingEnum,
          message.cornerEnum,
          message.typographyEnum,
          'personaBranding' in message || 'businessName' in message
            ? {
                ...('personaBranding' in message
                  ? { personaBranding: message.personaBranding ?? null }
                  : {}),
                ...('businessName' in message
                  ? { businessName: message.businessName ?? '' }
                  : {}),
              }
            : undefined,
          message.customColorSets,
        );
      }
      postSandboxSyncedToShell(blocks.length, message.activePageId ?? null);
      break;
    }
    case BridgeMessageType.CONTENT_PATCH:
      handleContentPatchMessage(message);
      break;
    case BridgeMessageType.MEDIA_PATCH:
      handleMediaPatchMessage(message);
      break;
    case BridgeMessageType.DESIGN_TOKENS:
      handleDesignTokensMessage(message);
      break;
    default: {
      const _exhaustive: never = message;
      return _exhaustive;
    }
  }
}

function onMessage(event: MessageEvent): void {
  if (!isAllowedShellOrigin(event.origin)) {
    return;
  }
  if (!isShellToSandboxMessage(event.data)) {
    return;
  }

  handleShellMessage(event.data);
}

function flushPendingShellMessages(): void {
  const htmlQueued =
    typeof window !== 'undefined' ? window.__wsPendingShellMessages?.slice() ?? [] : [];
  if (typeof window !== 'undefined') {
    window.__wsPendingShellMessages = [];
  }
  const queued = [
    ...htmlQueued.filter((message): message is ShellToSandboxMessage =>
      isShellToSandboxMessage(message),
    ),
    ...pendingShellMessages.splice(0, pendingShellMessages.length),
  ];
  for (const message of queued) {
    handleShellMessage(message);
  }
}

/**
 * Mount the sandbox postMessage bridge (studio-shell → preview runtime).
 * Returns a teardown function for HMR / strict mode double-mount.
 */
export function mountSandboxBridge(): () => void {
  bridgeMounted = true;
  window.removeEventListener('message', captureEarlyShellMessage);
  flushPendingShellMessages();
  window.addEventListener('message', onMessage);
  return () => {
    bridgeMounted = false;
    window.removeEventListener('message', onMessage);
    window.addEventListener('message', captureEarlyShellMessage);
  };
}
